import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useFirestore } from '../../contexts/FirestoreContext';
import { collection, query, where, orderBy, onSnapshot, addDoc, updateDoc, doc, serverTimestamp, getDoc, deleteDoc, Timestamp } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { Button, Input, Select, Table, Badge, Space, Modal, Form, Empty, Tooltip, Tag, Popconfirm, DatePicker, Alert } from 'antd';
import { PlusOutlined, SearchOutlined, LockOutlined, CheckCircleOutlined, ClockCircleOutlined, EditOutlined, DeleteOutlined, MoreOutlined, CalendarOutlined, UserOutlined, FlagOutlined, AimOutlined, ProjectOutlined } from '@ant-design/icons';
import { ROLES, ROLE_PERMISSIONS } from '../../models/role';
import { createIncidentLog, getUserFullName, ACTIVITY_TYPES } from '../../utils/activityLogger';
import { toDate, formatDate, getTimeRemaining, isOverdue as isDateOverdue } from '../../utils/dateUtils';
import MilestoneModal from '../timeline/MilestoneModal';
import { useMilestones, useMilestoneOperations } from '../../contexts/MilestoneDataContext';
import { getRegulatoryDeadlines, updateRegulatoryDeadlineStatus } from '../../utils/regulatoryDeadlines';

const { Option } = Select;

const TasksTab = ({ incidentId }) => {
  const { currentUser } = useAuth();
  const { db } = useFirestore();
  const [tasks, setTasks] = useState([]);
  const [regulatoryDeadlines, setRegulatoryDeadlines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isDependencyModalVisible, setIsDependencyModalVisible] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [editingTask, setEditingTask] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  // 🎯 Milestone Modal State
  const [isMilestoneModalVisible, setIsMilestoneModalVisible] = useState(false);
  const [editingMilestone, setEditingMilestone] = useState(null);
  const [isMilestoneEditMode, setIsMilestoneEditMode] = useState(false);
  const [form] = Form.useForm();
  const [dependencyForm] = Form.useForm();
  const [filters, setFilters] = useState({
    status: 'all',
    role: 'all',
    owner: 'all',
    search: '',
    type: 'all' // 'all', 'tasks', 'milestones'
  });
  const [userOrgId, setUserOrgId] = useState(null);

  // 🎯 Use shared milestone data
  const { milestones, loading: milestonesLoading } = useMilestones({ incidentId });
  const { updateMilestone, deleteMilestone } = useMilestoneOperations();

  const statusOptions = [
    { value: 'not_started', label: 'Not Started', color: 'default' },
    { value: 'in_progress', label: 'In Progress', color: 'processing' },
    { value: 'complete', label: 'Complete', color: 'success' },
    { value: 'blocked', label: 'Blocked', color: 'error' }
  ];

  // Milestone status options (Title Case)
  const milestoneStatusOptions = [
    { value: 'Not Started', label: 'Not Started', color: 'default' },
    { value: 'In Progress', label: 'In Progress', color: 'processing' },
    { value: 'Complete', label: 'Complete', color: 'success' },
    { value: 'Blocked', label: 'Blocked', color: 'error' }
  ];

  // Type filter options
  const typeOptions = [
    { value: 'all', label: 'All Items' },
    { value: 'tasks', label: 'Tasks Only' },
    { value: 'milestones', label: 'Milestones Only' },
    { value: 'regulatory', label: 'Regulatory Deadlines Only' }
  ];

  const roleOptions = [
    { value: ROLES.CLIENT_SERVICE, label: ROLE_PERMISSIONS[ROLES.CLIENT_SERVICE].name },
    { value: ROLES.CMT_ADMIN, label: ROLE_PERMISSIONS[ROLES.CMT_ADMIN].name },
    { value: ROLES.CMT_LEADER, label: ROLE_PERMISSIONS[ROLES.CMT_LEADER].name },
    { value: ROLES.CMT_LEADER_ALT, label: ROLE_PERMISSIONS[ROLES.CMT_LEADER_ALT].name },
    { value: ROLES.COMMS_MEDIA, label: ROLE_PERMISSIONS[ROLES.COMMS_MEDIA].name },
    { value: ROLES.FACILITIES, label: ROLE_PERMISSIONS[ROLES.FACILITIES].name },
    { value: ROLES.FINANCE, label: ROLE_PERMISSIONS[ROLES.FINANCE].name },
    { value: ROLES.IT_TECH, label: ROLE_PERMISSIONS[ROLES.IT_TECH].name },
    { value: ROLES.LEGAL, label: ROLE_PERMISSIONS[ROLES.LEGAL].name },
    { value: ROLES.OTHER, label: ROLE_PERMISSIONS[ROLES.OTHER].name },
    { value: ROLES.SAFETY_SECURITY, label: ROLE_PERMISSIONS[ROLES.SAFETY_SECURITY].name },
    { value: ROLES.TALENT_HR, label: ROLE_PERMISSIONS[ROLES.TALENT_HR].name }
  ];

  useEffect(() => {
    // Get user's orgId
    const fetchUserOrgId = async () => {
      try {
        const userDocRef = doc(db, 'c3_users', currentUser.uid);
        const userDoc = await getDoc(userDocRef);
        if (userDoc.exists()) {
          setUserOrgId(userDoc.data().orgId);
        }
      } catch (error) {
        console.error('Error fetching user orgId:', error);
      }
    };
    fetchUserOrgId();
  }, [currentUser.uid, db]);

  useEffect(() => {
    if (!incidentId || !userOrgId) return;

    const tasksRef = collection(db, 'c3_tasks');
    const q = query(
      tasksRef,
      where('incidentId', '==', incidentId),
      where('orgId', '==', userOrgId),
      where('deleted', '==', false), // Exclude soft-deleted tasks
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(q, 
      (snapshot) => {
        const taskList = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setTasks(taskList);
        setLoading(false);
      },
      (error) => {
        console.error('Error fetching tasks:', error);
        setError('Failed to load tasks');
        setLoading(false);
        toast.error('Failed to load tasks');
      }
    );

    return () => unsubscribe();
  }, [incidentId, userOrgId, db]);

  // Load regulatory deadlines
  useEffect(() => {
    if (!incidentId || !userOrgId) return;

    const loadRegulatoryDeadlines = async () => {
      try {
        const deadlines = await getRegulatoryDeadlines(incidentId, userOrgId, db, {
          includeDeleted: false,
          sortBy: 'deadlineTime',
          sortDirection: 'asc'
        });
        
        // Add type and assignment info for unified display
        const enhancedDeadlines = deadlines.map(deadline => ({
          ...deadline,
          type: 'regulatory',
          name: `Notify ${deadline.regulator}`,
          title: `Notify ${deadline.regulator}`,
          assignedRole: 'cmt_leader', // Auto-assign to CMT Leader
          owner: 'CMT Leader',
          dueDate: deadline.deadlineTime,
          priority: deadline.urgencyLevel || 'normal'
        }));
        
        setRegulatoryDeadlines(enhancedDeadlines);
      } catch (error) {
        console.error('Error loading regulatory deadlines:', error);
        toast.error('Failed to load regulatory deadlines');
      }
    };

    loadRegulatoryDeadlines();
  }, [incidentId, userOrgId, db]);

  const handleCreateTask = async (values) => {
    if (!userOrgId) {
      toast.error('Organisation ID not found');
      return;
    }

    try {
      const tasksRef = collection(db, 'c3_tasks');
      
      // Filter out undefined values to prevent Firestore errors
      const filteredValues = Object.fromEntries(
        Object.entries(values).filter(([_, value]) => value !== undefined)
      );
      
      // Validate prerequisites if provided
      const prerequisites = values.prerequisites || [];
      if (prerequisites.length > 0) {
        const validationErrors = validatePrerequisites(null, prerequisites); // null for new task
        if (validationErrors.length > 0) {
          toast.error(`Invalid prerequisites: ${validationErrors.join(', ')}`);
          return;
        }
      }
      
      const taskData = {
        ...filteredValues,
        name: values.name,
        title: values.name, // Ensure both fields exist for timeline compatibility
        incidentId,
        orgId: userOrgId,
        createdAt: serverTimestamp(),
        createdBy: currentUser.uid,
        status: values.status || 'not_started',
        prerequisites: prerequisites,
        dueDate: values.dueDate ? Timestamp.fromDate(new Date(values.dueDate)) : null
      };
      
      const docRef = await addDoc(tasksRef, taskData);
      setIsModalVisible(false);
      form.resetFields();
      toast.success('Task created successfully');
      
      // Enhanced audit trail logging for task creation
      try {
        const userFullName = await getUserFullName(db, currentUser.uid, currentUser.email);
        
        // Analyze task creation context
        const context = {
          hasPrerequisites: prerequisites.length > 0,
          prerequisiteCount: prerequisites.length,
          hasDueDate: !!values.dueDate,
          isDueSoon: values.dueDate ? (new Date(values.dueDate) - new Date()) < (24 * 60 * 60 * 1000) : false,
          assignedRole: values.assignedRole,
          owner: values.owner,
          isUrgent: values.priority === 'critical',
          creationMethod: 'manual'
        };
        
        await createIncidentLog({
          incidentId,
          orgId: userOrgId,
          action: ACTIVITY_TYPES.TASK_CREATED,
          details: `Task created: "${values.name || values.title}"${context.hasPrerequisites ? ` with ${context.prerequisiteCount} prerequisites` : ''}${context.isDueSoon ? ' (DUE WITHIN 24 HOURS)' : ''}`,
          userId: currentUser.uid,
          userEmail: currentUser.email,
          userFullName,
          priority: context.isUrgent || context.isDueSoon ? 'high' : 'normal',
          metadata: {
            taskId: docRef.id,
            taskName: values.name || values.title,
            assignedRole: values.assignedRole,
            owner: values.owner,
            dueDate: values.dueDate ? new Date(values.dueDate).toISOString() : null,
            description: values.description,
            context,
            timestamp: new Date().toISOString(),
            organizationId: userOrgId,
            incidentContext: {
              incidentId,
              totalTasks: tasks.length + 1, // Include newly created task
              totalWithPrerequisites: tasks.filter(t => t.prerequisites?.length > 0).length + (prerequisites.length > 0 ? 1 : 0)
            }
          }
        });
      } catch (logError) {
        console.error('Failed to log task creation:', logError);
      }
    } catch (error) {
      console.error('Error creating task:', error);
      toast.error('Failed to create task');
    }
  };

  const handleEditTask = async (values) => {
    if (!editingTask) return;

    try {
      // Validate prerequisites if provided
      const prerequisites = values.prerequisites || editingTask.prerequisites || [];
      if (prerequisites.length > 0) {
        const validationErrors = validatePrerequisites(editingTask.id, prerequisites);
        if (validationErrors.length > 0) {
          toast.error(`Invalid prerequisites: ${validationErrors.join(', ')}`);
          return;
        }
      }
      
      const taskRef = doc(db, 'c3_tasks', editingTask.id);
      const taskData = {
        ...values,
        name: values.name,
        title: values.name, // Ensure both fields exist for timeline compatibility
        prerequisites: prerequisites,
        updatedAt: serverTimestamp(),
        dueDate: values.dueDate ? Timestamp.fromDate(new Date(values.dueDate)) : null
      };
      
      await updateDoc(taskRef, taskData);
      setIsModalVisible(false);
      setIsEditMode(false);
      setEditingTask(null);
      form.resetFields();
      toast.success('Task updated successfully');
      
      // Log task update
      try {
        const userFullName = await getUserFullName(db, currentUser.uid, currentUser.email);
        await createIncidentLog({
          incidentId,
          orgId: userOrgId,
          action: ACTIVITY_TYPES.TASK_UPDATED,
          details: `Task updated: "${values.name || values.title}"`,
          userId: currentUser.uid,
          userEmail: currentUser.email,
          userFullName,
          priority: 'normal',
          metadata: {
            taskId: editingTask.id,
            taskName: values.name || values.title,
            assignedRole: values.assignedRole,
            owner: values.owner,
            dueDate: values.dueDate ? new Date(values.dueDate).toISOString() : null,
            changes: 'Task details updated'
          }
        });
      } catch (logError) {
        console.error('Failed to log task update:', logError);
      }
    } catch (error) {
      console.error('Error updating task:', error);
      toast.error('Failed to update task');
    }
  };

  const handleDeleteTask = async (taskId) => {
    try {
      const task = tasks.find(t => t.id === taskId);
      const taskRef = doc(db, 'c3_tasks', taskId);
      
      // Soft delete the task (consistent with milestones and regulatory deadlines)
      await updateDoc(taskRef, {
        deleted: true,
        deletedAt: serverTimestamp(),
        deletedBy: currentUser.uid
      });
      
      // Clean up any dependencies on this task
      await cleanupTaskDependencies(taskId);
      
      toast.success('Task deleted successfully');
      
      // Enhanced audit trail logging for task deletion
      try {
        const userFullName = await getUserFullName(db, currentUser.uid, currentUser.email);
        
        // Analyze deletion impact
        const dependentTasks = tasks.filter(t => t.prerequisites?.includes(taskId));
        const deletionImpact = {
          hadDependentTasks: dependentTasks.length > 0,
          dependentTaskCount: dependentTasks.length,
          wasOverdue: isTaskOverdue(task),
          wasComplete: task.status === 'complete',
          wasBlocked: task.status === 'blocked',
          hadPrerequisites: task.prerequisites?.length > 0,
          prerequisiteCount: task.prerequisites?.length || 0
        };
        
        await createIncidentLog({
          incidentId,
          orgId: userOrgId,
          action: ACTIVITY_TYPES.TASK_DELETED,
          details: `Task deleted: "${task?.name || task?.title || 'Unknown Task'}"${deletionImpact.hadDependentTasks ? ` (affected ${deletionImpact.dependentTaskCount} dependent tasks)` : ''}${deletionImpact.wasOverdue ? ' [WAS OVERDUE]' : ''}`,
          userId: currentUser.uid,
          userEmail: currentUser.email,
          userFullName,
          priority: deletionImpact.hadDependentTasks ? 'high' : 'normal',
          metadata: {
            taskId: taskId,
            taskName: task?.name || task?.title || 'Unknown Task',
            assignedRole: task?.assignedRole,
            owner: task?.owner,
            status: task?.status,
            dueDate: task?.dueDate ? formatDate(task.dueDate) : null,
            deletionImpact,
            dependentTasks: dependentTasks.map(t => ({
              id: t.id,
              name: t.name || t.title,
              status: t.status
            })),
            timestamp: new Date().toISOString(),
            cleanupPerformed: deletionImpact.hadDependentTasks,
            organizationId: userOrgId
          }
        });
      } catch (logError) {
        console.error('Failed to log task deletion:', logError);
      }
    } catch (error) {
      console.error('Error deleting task:', error);
      toast.error('Failed to delete task');
    }
  };

  const openEditModal = (item) => {
    // Check if it's a milestone, regulatory deadline, or task
    if (item.type === 'milestone') {
      // Use milestone edit modal
      handleEditMilestone(item);
    } else if (item.type === 'regulatory') {
      // Regulatory deadlines are not editable through this interface
      // They should be managed through the Regulatory Compliance tab
      toast.info('Regulatory deadlines can be managed through the Regulatory Compliance tab');
    } else if (item.type === 'regulatory_notification') {
      // Regulatory notification tasks can be edited like regular tasks
      setEditingTask(item);
      setIsEditMode(true);
      setIsModalVisible(true);
    } else {
      // Use task edit modal
      setEditingTask(item);
      setIsEditMode(true);
      setIsModalVisible(true);
      
      // Pre-populate form with task data
      form.setFieldsValue({
        name: item.name,
        description: item.description,
        assignedRole: item.assignedRole,
        owner: item.owner,
        dueDate: item.dueDate ? toDate(item.dueDate)?.toISOString().split('T')[0] : null
      });
    }
  };

  const closeModal = () => {
    setIsModalVisible(false);
    setIsEditMode(false);
    setEditingTask(null);
    form.resetFields();
  };

  // Helper function to format status for display
  const formatStatusForDisplay = (status) => {
    const statusMap = {
      'not_started': 'Not Started',
      'in_progress': 'In Progress', 
      'complete': 'Complete',
      'completed': 'Complete',
      'blocked': 'Blocked',
      'cancelled': 'Cancelled',
      'on_hold': 'On Hold'
    };
    return statusMap[status] || status;
  };

  const handleStatusChange = async (itemId, newStatus) => {
    try {
      // Find the item (could be task, milestone, or regulatory deadline)
      const task = tasks.find(t => t.id === itemId);
      const milestone = milestones.find(m => m.id === itemId);
      const regulatory = regulatoryDeadlines.find(r => r.id === itemId);
      const item = task || milestone || regulatory;
      
      if (!item) {
        console.error('Item not found:', itemId);
        return;
      }

      // Handle task status change
      if (task) {

      // Check if prerequisites are met
      const unmetPrerequisites = task.prerequisites?.filter(prereqId => {
        const prereqTask = tasks.find(t => t.id === prereqId);
        return !prereqTask || prereqTask.status !== 'complete';
      });

      if (newStatus !== 'not_started' && unmetPrerequisites?.length > 0) {
        toast.error('Cannot update status: prerequisites not met');
        return;
      }

      const taskRef = doc(db, 'c3_tasks', itemId);
      const oldStatus = task.status;
      await updateDoc(taskRef, {
        status: newStatus,
        updatedAt: serverTimestamp()
      });
      
      // Check if this is a regulatory notification task
      const isRegulatoryTask = task.type === 'regulatory_notification';
      toast.success(isRegulatoryTask ? 'Regulatory task status updated' : 'Task status updated');
      
      // 🔥 LIVE CHAT AUTO-MESSAGE: Send automatic message when regulatory task is completed
      if (isRegulatoryTask && newStatus === 'complete') {
        try {
          const userFullName = await getUserFullName(db, currentUser.uid, currentUser.email);
          
          // Add automatic live chat message for regulatory task completion
          await addDoc(collection(db, 'c3_incidentChats'), {
            incidentId,
            message: `📋 Regulatory notification completed: ${task.regulator} - ${task.regulatoryTrigger}`,
            timestamp: serverTimestamp(),
            authorId: 'system',
            authorName: 'System',
            firstName: '',
            lastName: '',
            role: 'system',
            orgId: userOrgId,
            isAutomatic: true,
            completedBy: userFullName,
            regulatoryData: {
              regulator: task.regulator,
              trigger: task.regulatoryTrigger,
              taskId: itemId
            }
          });
          

        } catch (chatError) {
          console.error('❌ Failed to send automatic live chat message:', chatError);
          // Don't fail the status update if chat fails
        }
      }
      
      // Enhanced audit trail logging for task status changes
      try {
        const userFullName = await getUserFullName(db, currentUser.uid, currentUser.email);
        
        // Calculate impact of status change
        const impact = {
          wasBlocked: oldStatus === 'blocked',
          isNowBlocked: newStatus === 'blocked',
          wasComplete: oldStatus === 'complete',
          isNowComplete: newStatus === 'complete',
          dependentTasks: tasks.filter(t => t.prerequisites?.includes(itemId)).length,
          prerequisites: task.prerequisites?.length || 0,
          isOverdue: isTaskOverdue(task)
        };
        
        // Determine priority based on impact
        let priority = 'normal';
        if (newStatus === 'complete' && impact.dependentTasks > 0) priority = 'high';
        if (newStatus === 'blocked') priority = 'high';
        if (impact.isOverdue) priority = 'critical';
        
        await createIncidentLog({
          incidentId,
          orgId: userOrgId,
          action: isRegulatoryTask ? 'regulatory_task_updated' : ACTIVITY_TYPES.TASK_STATUS_CHANGED,
          details: isRegulatoryTask ?
            `Regulatory task status changed: "${task.name || task.title}" from "${formatStatusForDisplay(oldStatus)}" to "${formatStatusForDisplay(newStatus)}"${impact.isOverdue ? ' [OVERDUE]' : ''}` :
            `Task status changed: "${task.name || task.title}" from "${formatStatusForDisplay(oldStatus)}" to "${formatStatusForDisplay(newStatus)}"${impact.dependentTasks > 0 ? ` (affects ${impact.dependentTasks} dependent tasks)` : ''}${impact.isOverdue ? ' [OVERDUE]' : ''}`,
          userId: currentUser.uid,
          userEmail: currentUser.email,
          userFullName,
          priority,
          metadata: {
            taskId: itemId,
            taskName: task.name || task.title,
            oldStatus: oldStatus,
            newStatus: newStatus,
            changeType: 'status',
            impact,
            assignedRole: task.assignedRole,
            owner: task.owner,
            dueDate: task.dueDate ? formatDate(task.dueDate) : null,
            timestamp: new Date().toISOString(),
            changeReason: impact.wasBlocked && newStatus !== 'blocked' ? 'prerequisite_completed' : 
                         !impact.wasBlocked && newStatus === 'blocked' ? 'prerequisite_incomplete' :
                         'manual_update',
            // Add regulatory-specific metadata
            ...(isRegulatoryTask && {
              isRegulatoryTask: true,
              regulator: task.regulator,
              regulatoryTrigger: task.regulatoryTrigger,
              notificationMethod: task.notificationMethod
            })
          }
        });
        } catch (logError) {
          console.error('Failed to log task status change:', logError);
        }
      }
      // Handle milestone status change
      else if (milestone) {
        // Normalize status for milestones (Title Case)
        const normalizedStatus = newStatus === 'not_started' ? 'Not Started' :
                                newStatus === 'in_progress' ? 'In Progress' :
                                newStatus === 'complete' ? 'Complete' :
                                newStatus === 'blocked' ? 'Blocked' : newStatus;
        
        const oldStatus = milestone.status;
        
        // Use milestone update function from Cloud Function
        await updateMilestone(itemId, {
          status: normalizedStatus
        });

        toast.success('Milestone status updated successfully');

        // Log the status change
        try {
          const userFullName = await getUserFullName(db, currentUser.uid, currentUser.email);
          
          await createIncidentLog(db, incidentId, {
            type: ACTIVITY_TYPES.MILESTONE_UPDATED,
            action: 'Milestone Status Updated',
            description: `Milestone "${milestone.title}" status changed from "${oldStatus}" to "${normalizedStatus}"`,
            performedBy: userFullName,
            metadata: {
              milestoneId: itemId,
              title: milestone.title,
              oldStatus: oldStatus,
              newStatus: normalizedStatus,
              dueDate: milestone.dueDate ? toDate(milestone.dueDate)?.toISOString() || null : null,
              timestamp: new Date().toISOString()
            }
          });
        } catch (logError) {
          console.error('Failed to log milestone status update:', logError);
        }
      }
      // Handle regulatory deadline status change
      else if (regulatory) {
        // Normalize status for regulatory deadlines (map to regulatory status format)
        const normalizedStatus = newStatus === 'not_started' ? 'pending' :
                                newStatus === 'in_progress' ? 'in_progress' :
                                newStatus === 'complete' ? 'completed' :
                                newStatus === 'blocked' ? 'overdue' : newStatus;
        
        const oldStatus = regulatory.status;
        
        // Use regulatory deadline update function
        await updateRegulatoryDeadlineStatus(itemId, normalizedStatus, userOrgId, currentUser.uid, db);

        toast.success('Regulatory deadline status updated successfully');

        // 🔥 LIVE CHAT AUTO-MESSAGE: Send automatic message when regulatory deadline is completed
        if (normalizedStatus === 'completed') {
          try {
            const userFullName = await getUserFullName(db, currentUser.uid, currentUser.email);
            
            // Add automatic live chat message for regulatory completion
            await addDoc(collection(db, 'c3_incidentChats'), {
              incidentId,
              message: `📋 Regulatory notification completed: ${regulatory.regulator} - ${regulatory.trigger}`,
              timestamp: serverTimestamp(),
              authorId: 'system',
              authorName: 'System',
              firstName: '',
              lastName: '',
              role: 'system',
              orgId: userOrgId,
              isAutomatic: true,
              completedBy: userFullName,
              regulatoryData: {
                regulator: regulatory.regulator,
                trigger: regulatory.trigger,
                deadlineId: itemId
              }
            });
            

          } catch (chatError) {
            console.error('❌ Failed to send automatic live chat message:', chatError);
            // Don't fail the status update if chat fails
          }
        }

        // Log the status change for regulatory completion
        try {
          const userFullName = await getUserFullName(db, currentUser.uid, currentUser.email);
          
          await createIncidentLog({
            incidentId,
            orgId: userOrgId,
            action: 'regulatory_deadline_updated',
            details: `Regulatory deadline status changed: "${regulatory.name}" from "${oldStatus}" to "${normalizedStatus}"`,
            userId: currentUser.uid,
            userEmail: currentUser.email,
            userFullName,
            priority: normalizedStatus === 'completed' ? 'high' : 'normal',
            metadata: {
              regulatoryDeadlineId: itemId,
              name: regulatory.name,
              regulator: regulatory.regulator,
              trigger: regulatory.trigger,
              oldStatus: oldStatus,
              newStatus: normalizedStatus,
              dueDate: regulatory.dueDate ? toDate(regulatory.dueDate)?.toISOString() || null : null,
              timestamp: new Date().toISOString()
            }
          });
        } catch (logError) {
          console.error('Failed to log regulatory deadline status update:', logError);
        }
      }
    } catch (error) {
      console.error('Error updating item status:', error);
      const itemType = tasks.find(t => t.id === itemId) ? 'task' : 
                       milestones.find(m => m.id === itemId) ? 'milestone' :
                       regulatoryDeadlines.find(r => r.id === itemId) ? 'regulatory deadline' : 'item';
      toast.error(`Failed to update ${itemType} status`);
    }
  };

  const handleAddDependency = async (values) => {
    try {
      const { prerequisiteTaskId } = values;
      const taskRef = doc(db, 'c3_tasks', selectedTask.id);
      
      // Check for circular dependencies
      const isCircular = checkCircularDependency(selectedTask.id, prerequisiteTaskId);
      if (isCircular) {
        toast.error('Cannot add dependency: Would create a circular dependency');
        return;
      }

      await updateDoc(taskRef, {
        prerequisites: [...(selectedTask.prerequisites || []), prerequisiteTaskId],
        updatedAt: serverTimestamp()
      });

      setIsDependencyModalVisible(false);
      dependencyForm.resetFields();
      toast.success('Dependency added successfully');
      createIncidentLog(db, currentUser.uid, 'task', 'dependency_added', { id: selectedTask.id, prerequisiteId: prerequisiteTaskId });
    } catch (error) {
      console.error('Error adding dependency:', error);
      toast.error('Failed to add dependency');
    }
  };

  const checkCircularDependency = (taskId, prerequisiteId) => {
    // Create a temporary dependency map with the new dependency added
    const tempDependencies = new Map();
    
    // Build current dependency map
    tasks.forEach(task => {
      if (task.prerequisites && task.prerequisites.length > 0) {
        tempDependencies.set(task.id, task.prerequisites);
      }
    });
    
    // Add the proposed new dependency
    const currentPrereqs = tempDependencies.get(taskId) || [];
    tempDependencies.set(taskId, [...currentPrereqs, prerequisiteId]);
    
    // Use robust circular dependency detection algorithm
    const visited = new Set();
    const recursionStack = new Set();
    
    const dfs = (currentId, path = []) => {
      if (recursionStack.has(currentId)) {
        // Found a cycle
        console.log('🔄 Circular dependency detected:', [...path, currentId]);
        return true;
      }
      
      if (visited.has(currentId)) {
        return false;
      }
      
      visited.add(currentId);
      recursionStack.add(currentId);
      
      const prerequisites = tempDependencies.get(currentId) || [];
      for (const prereqId of prerequisites) {
        if (dfs(prereqId, [...path, currentId])) {
          return true;
        }
      }
      
      recursionStack.delete(currentId);
      return false;
    };
    
    // Check all tasks to ensure no circular dependencies exist
    for (const task of tasks) {
      if (!visited.has(task.id)) {
        if (dfs(task.id)) {
          return true;
        }
      }
    }
    
    return false;
  };

  const getTaskStatus = (task) => {
    if (task.status === 'complete') return 'success';
    if (task.status === 'in_progress') return 'processing';
    if (task.status === 'blocked') return 'error';

    // Check prerequisites
    const unmetPrerequisites = task.prerequisites?.filter(prereqId => {
      const prereqTask = tasks.find(t => t.id === prereqId);
      return !prereqTask || prereqTask.status !== 'complete';
    });

    if (unmetPrerequisites?.length > 0) {
      return 'default';
    }

    return 'warning';
  };

  // Helper function to check if task is overdue (using standardized date utility)
  const isTaskOverdue = (task) => {
    if (!task.dueDate || task.status === 'complete') return false;
    return isDateOverdue(task.dueDate);
  };

  // Comprehensive prerequisite validation
  const validatePrerequisites = (taskId, prerequisites = []) => {
    const errors = [];
    
    // Check for self-dependency
    if (prerequisites.includes(taskId)) {
      errors.push('Task cannot depend on itself');
    }
    
    // Check for non-existent prerequisites
    const invalidPrereqs = prerequisites.filter(prereqId => 
      !tasks.find(t => t.id === prereqId)
    );
    if (invalidPrereqs.length > 0) {
      errors.push(`Invalid prerequisites: ${invalidPrereqs.length} task(s) not found`);
    }
    
    // Check for circular dependencies
    if (prerequisites.length > 0) {
      for (const prereqId of prerequisites) {
        if (checkCircularDependency(taskId, prereqId)) {
          errors.push(`Circular dependency detected with prerequisite task`);
          break;
        }
      }
    }
    
    return errors;
  };

  // Comprehensive dependency analysis for alerts and warnings
  const analyzeDependencyIssues = useCallback(() => {
    const issues = [];
    
    // Analyze each task for dependency problems
    tasks.forEach(task => {
      if (!task.prerequisites || task.prerequisites.length === 0) return;
      
      // Check for blocked tasks (incomplete prerequisites)
      const blockedPrereqs = task.prerequisites.filter(prereqId => {
        const prereqTask = tasks.find(t => t.id === prereqId);
        return prereqTask && prereqTask.status !== 'complete';
      });
      
      if (blockedPrereqs.length > 0 && task.status !== 'complete') {
        const prereqNames = blockedPrereqs.map(id => {
          const prereqTask = tasks.find(t => t.id === id);
          return prereqTask?.name || prereqTask?.title || 'Unknown Task';
        });
        
        issues.push({
          type: 'blocked',
          severity: 'warning',
          taskId: task.id,
          taskName: task.name || task.title,
          message: `Task blocked by ${blockedPrereqs.length} incomplete prerequisite(s): ${prereqNames.join(', ')}`,
          prereqTasks: blockedPrereqs
        });
      }
      
      // Check for overdue tasks with incomplete prerequisites
      if (isTaskOverdue(task) && blockedPrereqs.length > 0) {
        issues.push({
          type: 'overdue_blocked',
          severity: 'critical',
          taskId: task.id,
          taskName: task.name || task.title,
          message: `Overdue task still blocked by incomplete prerequisites`,
          prereqTasks: blockedPrereqs
        });
      }
      
      // Check for missing prerequisites
      const missingPrereqs = task.prerequisites.filter(prereqId => 
        !tasks.find(t => t.id === prereqId)
      );
      
      if (missingPrereqs.length > 0) {
        issues.push({
          type: 'missing_prerequisite',
          severity: 'critical',
          taskId: task.id,
          taskName: task.name || task.title,
          message: `References ${missingPrereqs.length} deleted or missing prerequisite task(s)`,
          missingIds: missingPrereqs
        });
      }
      
      // Check for circular dependencies
      if (checkCircularDependency(task.id, task.prerequisites[0])) {
        issues.push({
          type: 'circular_dependency',
          severity: 'critical',
          taskId: task.id,
          taskName: task.name || task.title,
          message: `Part of a circular dependency chain`,
          prerequisites: task.prerequisites
        });
      }
    });
    
    return issues;
  }, [tasks, isTaskOverdue, checkCircularDependency]);

  // Get dependency issues for current tasks
  const dependencyIssues = useMemo(() => {
    return analyzeDependencyIssues();
  }, [analyzeDependencyIssues]);

  // Clean up prerequisites when tasks are deleted
  const cleanupTaskDependencies = async (deletedTaskId) => {
    try {
      // Find all tasks that depend on the deleted task
      const dependentTasks = tasks.filter(task => 
        task.prerequisites && task.prerequisites.includes(deletedTaskId)
      );
      
      // Remove the deleted task from their prerequisites
      const updatePromises = dependentTasks.map(async (task) => {
        const newPrerequisites = task.prerequisites.filter(id => id !== deletedTaskId);
        const taskRef = doc(db, 'c3_tasks', task.id);
        
        await updateDoc(taskRef, {
          prerequisites: newPrerequisites,
          updatedAt: serverTimestamp()
        });
        
        console.log(`🧹 Cleaned up prerequisite ${deletedTaskId} from task ${task.id}`);
      });
      
      await Promise.all(updatePromises);
      
      if (dependentTasks.length > 0) {
        toast.success(`Cleaned up ${dependentTasks.length} dependent task(s)`);
      }
      
    } catch (error) {
      console.error('Error cleaning up task dependencies:', error);
      toast.error('Failed to clean up task dependencies');
    }
  };

  const columns = [
    {
      title: 'Task/Milestone Name',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <Space direction="vertical" size={4}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {/* Type indicator */}
            {record.type === 'milestone' ? (
              <CheckCircleOutlined 
                style={{ color: '#52c41a', fontSize: 16 }} 
                title="Milestone"
              />
            ) : (
              <ProjectOutlined 
                style={{ color: '#1890ff', fontSize: 16 }} 
                title="Task"
              />
            )}
            
            {record.prerequisites?.length > 0 && (
              <Tooltip title={`${record.prerequisites.length} prerequisite(s)`}>
                <LockOutlined style={{ color: getTaskStatus(record) === 'default' ? '#d9d9d9' : '#52c41a' }} />
              </Tooltip>
            )}
            
            <span style={{ 
              fontWeight: isTaskOverdue(record) ? 'bold' : 'normal',
              color: isTaskOverdue(record) ? '#ff4d4f' : 'inherit'
            }}>
              {text}
              {isTaskOverdue(record) && (
                <Tooltip title="This task is overdue!">
                  <span style={{ marginLeft: '8px', color: '#ff4d4f' }}>⚠️ OVERDUE</span>
                </Tooltip>
              )}
            </span>
          </div>
          
          {record.prerequisites?.length > 0 && (
            <Space size="small">
              {record.prerequisites.map((prereqId, index) => {
                const prereqTask = tasks.find(t => t.id === prereqId);
                return (
                  <Tooltip key={`${prereqId}-${index}`} title={prereqTask?.name || 'Unknown task'}>
                    <Tag color={prereqTask?.status === 'complete' ? 'success' : 'default'}>
                      {prereqTask?.name?.substring(0, 10) || '...'}
                    </Tag>
                  </Tooltip>
                );
              })}
            </Space>
          )}
        </Space>
      )
    },
    {
      title: 'Assigned Role',
      dataIndex: 'assignedRole',
      key: 'assignedRole',
      render: (role) => roleOptions.find(r => r.value === role)?.label || role
    },
    {
      title: 'Owner',
      dataIndex: 'owner',
      key: 'owner',
      render: (owner, record) => {
        if (!owner && record.autoCreated && record.type === 'regulatory_notification') {
          return <span style={{ color: '#888', fontStyle: 'italic' }}>Unassigned (Role-based)</span>;
        }
        return owner || '-';
      }
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status, record) => {
        // For milestones, use direct status editing
        if (record.type === 'milestone') {
          const milestoneStatusOptions = [
            { value: 'not_started', label: 'Not Started' },
            { value: 'in_progress', label: 'In Progress' },
            { value: 'complete', label: 'Complete' },
            { value: 'blocked', label: 'Blocked' }
          ];
          
          // Convert milestone status to lowercase for select value
          const selectValue = status === 'Not Started' ? 'not_started' :
                             status === 'In Progress' ? 'in_progress' :
                             status === 'Complete' ? 'complete' :
                             status === 'Blocked' ? 'blocked' : status.toLowerCase();
          
          return (
            <Select
              value={selectValue}
              onChange={(value) => handleStatusChange(record.id, value)}
              style={{ width: 120 }}
            >
              {milestoneStatusOptions.map(option => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          );
        }
        
        // For tasks, show status with dropdown
        const taskStatus = getTaskStatus(record);
        const statusOption = statusOptions.find(s => s.value === status);
        
        return (
          <Space direction="vertical" size={4}>
            <Space>
              <Badge 
                status={isTaskOverdue(record) ? 'error' : taskStatus} 
                text={statusOption?.label || status}
              />
              {taskStatus === 'default' && (
                <Tooltip title="Waiting for prerequisites">
                  <ClockCircleOutlined />
                </Tooltip>
              )}
              {isTaskOverdue(record) && (
                <Tooltip title="Task is overdue">
                  <ClockCircleOutlined style={{ color: '#ff4d4f' }} />
                </Tooltip>
              )}
            </Space>
            
            <Select
              value={record.status}
              onChange={(value) => handleStatusChange(record.id, value)}
              style={{ width: 120 }}
              disabled={getTaskStatus(record) === 'default'}
              size="small"
            >
              {statusOptions.map(option => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </Space>
        );
      }
    },
    {
      title: 'Due Date',
      dataIndex: 'dueDate',
      key: 'dueDate',
      render: (date, record) => {
        const formattedDate = formatDate(date, 'DD/MM/YYYY');
        const isOverdue = isTaskOverdue(record);
        const timeInfo = getTimeRemaining(date);
        
        return (
          <span style={{ 
            color: isOverdue ? '#ff4d4f' : 'inherit',
            fontWeight: isOverdue ? 'bold' : 'normal'
          }}>
            {formattedDate}
            {isOverdue && (
              <div style={{ fontSize: '11px', color: '#ff4d4f', fontWeight: 'bold' }}>
                {timeInfo.timeText.toUpperCase()}
              </div>
            )}
          </span>
        );
      }
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space wrap>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => openEditModal(record)}
            title={`Edit ${record.type === 'milestone' ? 'milestone' : record.type === 'regulatory' ? 'regulatory deadline' : record.type === 'regulatory_notification' ? 'regulatory task' : 'task'}`}
          >
            Edit
          </Button>
          <Popconfirm
            title={`Delete ${record.type === 'milestone' ? 'Milestone' : record.type === 'regulatory' ? 'Regulatory Deadline' : record.type === 'regulatory_notification' ? 'Regulatory Task' : 'Task'}`}
            description={`Are you sure you want to delete this ${record.type === 'milestone' ? 'milestone' : record.type === 'regulatory' ? 'regulatory deadline' : record.type === 'regulatory_notification' ? 'regulatory task' : 'task'}?`}
            onConfirm={() => record.type === 'milestone' ? handleDeleteMilestone(record.id) : 
                              record.type === 'regulatory' ? handleDeleteRegulatoryDeadline(record.id) : 
                              record.type === 'regulatory_notification' ? handleDeleteTask(record.id) :
                              handleDeleteTask(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              title={`Delete ${record.type === 'milestone' ? 'milestone' : record.type === 'regulatory' ? 'regulatory deadline' : record.type === 'regulatory_notification' ? 'regulatory task' : 'task'}`}
            >
              Delete
            </Button>
          </Popconfirm>
          {record.type !== 'milestone' && record.type !== 'regulatory' && (
            <Button
              type="link"
              onClick={() => {
                setSelectedTask(record);
                setIsDependencyModalVisible(true);
              }}
              title="Click here if this task cannot be commenced until another task is completed"
            >
              Add Dependency
            </Button>
          )}
        </Space>
      )
    }
  ];

  // 🎯 Combine tasks, milestones, and regulatory deadlines into unified list
  const combinedItems = useMemo(() => {
    const items = [];
    
    // Add tasks with type field
    tasks.forEach(task => {
      items.push({
        ...task,
        type: 'task',
        name: task.name || task.title, // Normalize name field
        dueDate: task.dueDate
      });
    });
    
    // Add milestones with type field
    milestones.forEach(milestone => {
      items.push({
        ...milestone,
        type: 'milestone',
        name: milestone.title, // Milestones use 'title' field
        dueDate: milestone.dueDate
      });
    });
    
    // Add regulatory deadlines with type field
    regulatoryDeadlines.forEach(deadline => {
      items.push({
        ...deadline,
        type: 'regulatory',
        name: deadline.name, // Already formatted in loading
        dueDate: deadline.dueDate
      });
    });
    
    return items;
  }, [tasks, milestones, regulatoryDeadlines]);

  // Filter combined items
  const filteredItems = combinedItems.filter(item => {
    return (
      // Type filter
      (filters.type === 'all' || 
       (filters.type === 'tasks' && item.type === 'task') ||
       (filters.type === 'milestones' && item.type === 'milestone') ||
       (filters.type === 'regulatory' && (item.type === 'regulatory' || item.type === 'regulatory_notification'))) &&
      // Status filter
      (filters.status === 'all' || item.status === filters.status) &&
      // Role filter (only apply to tasks)
      (filters.role === 'all' || item.type === 'milestone' || item.assignedRole === filters.role) &&
      // Owner filter (only apply to tasks)
      (filters.owner === 'all' || item.type === 'milestone' || item.owner === filters.owner) &&
      // Search filter
      (filters.search === '' || 
        item.name?.toLowerCase().includes(filters.search.toLowerCase()) ||
        item.description?.toLowerCase().includes(filters.search.toLowerCase()))
    );
  });

  // 🎯 Milestone editing handlers
  const handleEditMilestone = (milestone) => {
    setEditingMilestone(milestone);
    setIsMilestoneEditMode(true);
    setIsMilestoneModalVisible(true);
  };

  const handleDeleteMilestone = async (milestoneId) => {
    try {
      const milestone = milestones.find(m => m.id === milestoneId);
      await deleteMilestone(milestoneId);
      
      // Log the milestone deletion activity
      try {
        const userFullName = await getUserFullName(db, currentUser.uid, currentUser.email);
        
        await createIncidentLog(db, incidentId, {
          type: ACTIVITY_TYPES.MILESTONE_DELETED,
          action: 'Milestone Deleted',
          description: `Milestone "${milestone?.title || 'Unknown'}" was deleted`,
          performedBy: userFullName,
          metadata: {
            milestoneId: milestoneId,
            title: milestone?.title,
            status: milestone?.status,
            dueDate: milestone?.dueDate ? toDate(milestone.dueDate)?.toISOString() || null : null,
            timestamp: new Date().toISOString()
          }
        });
      } catch (logError) {
        console.warn('Failed to log milestone deletion:', logError);
      }
      
      toast.success('Milestone deleted successfully');
    } catch (error) {
      console.error('Error deleting milestone:', error);
      toast.error('Failed to delete milestone');
    }
  };

  const handleDeleteRegulatoryDeadline = async (deadlineId) => {
    try {
      const deadline = regulatoryDeadlines.find(d => d.id === deadlineId);
      
      // Use regulatory deadline update to mark as deleted (soft delete)
      await updateRegulatoryDeadlineStatus(deadlineId, 'deleted', userOrgId, currentUser.uid, db);
      
      // Refresh regulatory deadlines list
      const updatedDeadlines = await getRegulatoryDeadlines(incidentId, userOrgId, db, {
        includeDeleted: false,
        sortBy: 'deadlineTime',
        sortDirection: 'asc'
      });
      
      const enhancedDeadlines = updatedDeadlines.map(deadline => ({
        ...deadline,
        type: 'regulatory',
        name: `Notify ${deadline.regulator}`,
        title: `Notify ${deadline.regulator}`,
        assignedRole: 'cmt_leader',
        owner: 'CMT Leader',
        dueDate: deadline.deadlineTime,
        priority: deadline.urgencyLevel || 'normal'
      }));
      
      setRegulatoryDeadlines(enhancedDeadlines);
      
      // Log the regulatory deadline deletion activity
      try {
        const userFullName = await getUserFullName(db, currentUser.uid, currentUser.email);
        
        await createIncidentLog({
          incidentId,
          orgId: userOrgId,
          action: 'regulatory_deadline_deleted',
          details: `Regulatory deadline "${deadline?.name || 'Unknown'}" was deleted`,
          userId: currentUser.uid,
          userEmail: currentUser.email,
          userFullName,
          priority: 'normal',
          metadata: {
            regulatoryDeadlineId: deadlineId,
            name: deadline?.name,
            regulator: deadline?.regulator,
            trigger: deadline?.trigger,
            status: deadline?.status,
            dueDate: deadline?.dueDate ? toDate(deadline.dueDate)?.toISOString() || null : null,
            timestamp: new Date().toISOString()
          }
        });
      } catch (logError) {
        console.warn('Failed to log regulatory deadline deletion:', logError);
      }
      
      toast.success('Regulatory deadline deleted successfully');
    } catch (error) {
      console.error('Error deleting regulatory deadline:', error);
      toast.error('Failed to delete regulatory deadline');
    }
  };

  const handleMilestoneModalClose = () => {
    setIsMilestoneModalVisible(false);
    setEditingMilestone(null);
    setIsMilestoneEditMode(false);
  };

  // Check if any item (task or milestone) is overdue
  const isItemOverdue = (item) => {
    if (!item.dueDate || item.status === 'complete') return false;
    return isDateOverdue(item.dueDate);
  };

  // Update loading logic to account for both tasks and milestones
  const isLoading = loading || milestonesLoading;

  if (isLoading) {
    return (
      <div className="text-center py-4">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading tasks and milestones...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        {error}
      </div>
    );
  }

  return (
    <div className="tasks-container">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h3 className="h5 mb-0">Tasks & Milestones</h3>
        <Space>
          <Tooltip title="Create a new milestone - an important event or checkpoint to track. Milestones are not assigned to anyone, they simply mark significant moments in the crisis response timeline.">
            <Button
              icon={<AimOutlined />}
              onClick={() => setIsMilestoneModalVisible(true)}
              style={{ borderColor: '#39325b', color: '#39325b' }}
            >
              New Milestone
            </Button>
          </Tooltip>
        <Tooltip title="Create a new task for the crisis response. Tasks can be assigned to specific roles, given deadlines, and tracked for completion. Use dependencies to link related tasks.">
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setIsEditMode(false);
              setEditingTask(null);
              form.resetFields();
              setIsModalVisible(true);
            }}
            style={{ backgroundColor: '#39325b', borderColor: '#39325b' }}
          >
            New Task
          </Button>
        </Tooltip>
        </Space>
      </div>

      {/* Dependency Issue Alerts */}
      {dependencyIssues.length > 0 && (
        <div className="mb-3">
          {dependencyIssues
            .filter(issue => issue.severity === 'critical')
            .slice(0, 3) // Show max 3 critical issues
            .map((issue, index) => (
              <Alert
                key={`${issue.taskId}-${issue.type}`}
                type="error"
                message={`Critical Dependency Issue`}
                description={
                  <div>
                    <strong>{issue.taskName}:</strong> {issue.message}
                    {issue.type === 'missing_prerequisite' && (
                      <div style={{ marginTop: '4px', fontSize: '12px' }}>
                        <Button 
                          size="small" 
                          type="link" 
                          style={{ padding: '0', height: 'auto' }}
                          onClick={() => {
                            // Auto-fix: Remove missing prerequisites
                            const task = tasks.find(t => t.id === issue.taskId);
                            if (task) {
                              const validPrereqs = task.prerequisites.filter(id => 
                                tasks.find(t => t.id === id)
                              );
                              // Update task with cleaned prerequisites
                              const taskRef = doc(db, 'c3_tasks', task.id);
                              updateDoc(taskRef, { 
                                prerequisites: validPrereqs,
                                updatedAt: serverTimestamp() 
                              });
                              toast.success('Invalid prerequisites removed');
                            }
                          }}
                        >
                          Auto-fix: Remove invalid prerequisites
                        </Button>
                      </div>
                    )}
                  </div>
                }
                showIcon
                closable
                style={{ marginBottom: '8px' }}
              />
            ))}
          
          {dependencyIssues
            .filter(issue => issue.severity === 'warning')
            .slice(0, 2) // Show max 2 warning issues
            .map((issue, index) => (
              <Alert
                key={`${issue.taskId}-${issue.type}`}
                type="warning"
                message={`Task Blocked`}
                description={
                  <div>
                    <strong>{issue.taskName}:</strong> {issue.message}
                  </div>
                }
                showIcon
                closable
                style={{ marginBottom: '8px' }}
              />
            ))}
          
          {dependencyIssues.length > 5 && (
            <Alert
              type="info"
              message={`${dependencyIssues.length - 5} additional dependency issues found`}
              description="Use the dependency mapper for detailed analysis and resolution."
              showIcon
              style={{ marginBottom: '8px' }}
            />
          )}
        </div>
      )}

      <div className="filters-container mb-4">
        <Space wrap>
          <Tooltip title="Filter tasks by their completion status. Use this to focus on specific types of tasks (e.g., only overdue tasks or completed work).">
            <Select
              value={filters.status}
              onChange={(value) => setFilters(prev => ({ ...prev, status: value }))}
              style={{ width: 150 }}
            >
              <Option value="all">All Statuses</Option>
              {statusOptions.map(option => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </Tooltip>

          <Tooltip title="Filter tasks by assigned role or department. Use this to see tasks relevant to specific team members or functional areas.">
            <Select
              value={filters.role}
              onChange={(value) => setFilters(prev => ({ ...prev, role: value }))}
              style={{ width: 200 }}
            >
              <Option value="all">All Roles</Option>
              {roleOptions.map(option => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </Tooltip>

          <Tooltip title="Search for specific tasks by name or description. Type keywords to find relevant tasks quickly.">
            <Input
              placeholder="Search tasks..."
              prefix={<SearchOutlined />}
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              style={{ width: 200 }}
            />
          </Tooltip>
        </Space>
      </div>

      {filteredItems.length === 0 ? (
        <Empty
          description="No items match your current filters"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      ) : (
        <>
          {/* Overdue Items Alert Summary */}
          {filteredItems.some(item => isItemOverdue(item)) && (
            <div 
              style={{ 
                marginBottom: '24px',
                backgroundColor: '#fff2f0',
                border: '1px solid #ff4d4f',
                borderLeft: '4px solid #ff4d4f',
                borderRadius: '6px',
                padding: '16px'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
                <ClockCircleOutlined style={{ color: '#ff4d4f', fontSize: '18px', marginRight: '8px' }} />
                <strong style={{ fontSize: '16px', color: '#ff4d4f' }}>
                  🚨 Overdue Items ({filteredItems.filter(item => isItemOverdue(item)).length})
                </strong>
              </div>
              <div style={{ color: '#666', fontSize: '14px' }}>
                The following items are past their due date and require immediate attention:
              </div>
              <div style={{ marginTop: '8px' }}>
                {filteredItems.filter(item => isItemOverdue(item)).slice(0, 3).map((item, index) => (
                  <Tag key={index} color="red" style={{ marginBottom: '4px' }}>
                    {item.type === 'milestone' ? '🎯 ' : '📋 '}{item.name}
                  </Tag>
                ))}
                {filteredItems.filter(item => isItemOverdue(item)).length > 3 && (
                  <Tag color="red">+ {filteredItems.filter(item => isItemOverdue(item)).length - 3} more</Tag>
                )}
              </div>
            </div>
          )}
          <Table
            columns={columns}
            dataSource={filteredItems}
            rowKey="id"
            pagination={{ pageSize: 10 }}
            rowClassName={(record) => isItemOverdue(record) ? 'overdue-task-row' : ''}
          />
        </>
      )}

      <Modal
        title={isEditMode ? "Edit Task" : "New Task"}
        open={isModalVisible}
        onCancel={closeModal}
        onOk={() => form.submit()}
        okText={isEditMode ? "Update Task" : "Create Task"}
        cancelText="Cancel"
        confirmLoading={false}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={isEditMode ? handleEditTask : handleCreateTask}
        >
          <Form.Item
            name="name"
            label={<span style={{ fontWeight: 'bold' }}>Task Name</span>}
            rules={[{ required: true, message: 'Please enter task name' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="description"
            label={<span style={{ fontWeight: 'bold' }}>Description</span>}
          >
            <Input.TextArea rows={4} />
          </Form.Item>

          <Form.Item
            name="assignedRole"
            label={<span style={{ fontWeight: 'bold' }}>Assigned Role</span>}
            rules={[{ required: true, message: 'Please select a role' }]}
          >
            <Select>
              {roleOptions.map(option => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="owner"
            label={<span style={{ fontWeight: 'bold' }}>Owner</span>}
            rules={[{ 
              required: !(editingTask?.autoCreated && editingTask?.type === 'regulatory_notification'), 
              message: 'Please enter owner name' 
            }]}
          >
            <Input placeholder={
              editingTask?.autoCreated && editingTask?.type === 'regulatory_notification' 
                ? 'Unassigned (Role-based task)' 
                : 'Enter owner name'
            } />
          </Form.Item>

          <Form.Item
            name="dueDate"
            label={<span style={{ fontWeight: 'bold' }}>Due Date</span>}
          >
            <Input type="date" />
          </Form.Item>

          <Form.Item
            name="status"
            label={<span style={{ fontWeight: 'bold' }}>Status</span>}
            initialValue="not_started"
            rules={[{ required: true, message: 'Please select a status' }]}
          >
            <Select placeholder="Select task status">
              <Select.Option value="not_started">Not Started</Select.Option>
              <Select.Option value="in_progress">In Progress</Select.Option>
              <Select.Option value="complete">Complete</Select.Option>
              <Select.Option value="blocked">Blocked</Select.Option>
            </Select>
          </Form.Item>

          {/* Submit button now handled by modal footer */}
        </Form>
      </Modal>

      <Modal
        title="Add Dependency"
        open={isDependencyModalVisible}
        onCancel={() => {
          setIsDependencyModalVisible(false);
          dependencyForm.resetFields();
        }}
        onOk={() => dependencyForm.submit()}
        okText="Add Dependency"
        cancelText="Cancel"
        confirmLoading={false}
      >
        <Form
          form={dependencyForm}
          layout="vertical"
          onFinish={handleAddDependency}
        >
          <Form.Item
            name="prerequisiteTaskId"
            label="Prerequisite Task"
            rules={[{ required: true, message: 'Please select a prerequisite task' }]}
          >
            <Select>
              {tasks
                .filter(task => task.id !== selectedTask?.id)
                .map(task => (
                  <Option key={task.id} value={task.id}>
                    {task.name}
                  </Option>
                ))}
            </Select>
          </Form.Item>

          {/* Submit button now handled by modal footer */}
        </Form>
      </Modal>

      {/* 🎯 Milestone Modal */}
      <MilestoneModal
        visible={isMilestoneModalVisible}
        onClose={() => setIsMilestoneModalVisible(false)}
        onSave={(milestone) => {
          // Handle milestone save - refresh the page or update state
          window.location.reload(); // Simple refresh for now
        }}
        incidentId={incidentId}
        milestone={null}
      />
    </div>
  );
};

export default TasksTab; 